import { useEffect, useMemo, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import {
  VoicemailEventType,
  VoicemailTagsAlertEvent,
  VoicemailUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { produce } from 'immer';
import { InfiniteData, QueryClient, useQueryClient } from 'react-query';
import { TagsV2 } from '@frontend/api-tag';
import { VoicemailApi } from '@frontend/api-voicemails';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { AllCallsTable, usePhonePageSettingShallowStore } from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { VoicemailQueryData, VoicemailTableDataRow } from '../all-calls/types';
import { createVoicemailObject } from './voicemail-utils';

export const useVoicemailTagEvent = () => {
  const queryClient = useQueryClient();
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const { config: phonePageConfig } = usePhonePageSettingShallowStore('config');
  const pageSize = phonePageConfig[AllCallsTable.Voicemails].pageSize;
  const filters = phonePageConfig[AllCallsTable.Voicemails].filters;

  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const searchQuery = useMemo(() => `${JSON.stringify({ ...filters })}-${pageSize}`, [filters, pageSize]);

  const qnKeys = [selectedLocationIds, ...queryKeys.multiVoicemails(searchQuery)];

  const handleVoicemailTagEvent = (event: VoicemailTagsAlertEvent) => {
    const { voicemailMessageId, tagsNotificationPayload } = event;
    const tagIds = tagsNotificationPayload.map((tag) => tag.id);
    updateVoicemailTagsInVoicemails({
      voicemailMessageId,
      tagIds,
      mappedTags,
      queryClient,
      voicemailQueryKey: qnKeys,
    });
  };

  return { handleVoicemailTagEvent };
};

export const mutateVoicemailData = ({
  voicemailData,
  voicemailId,
  pageSize,
  bailIfNotFound = true,
  callbackFn,
}: {
  voicemailData?: InfiniteData<VoicemailQueryData>;
  voicemailId: string;
  pageSize: number;
  bailIfNotFound?: boolean;
  callbackFn: ({
    voicemailRow,
    voicemailIndexInPage,
    voicemailPage,
    originalData,
  }: {
    voicemailRow?: VoicemailTableDataRow;
    voicemailIndexInPage: number;
    voicemailPage?: VoicemailQueryData;
    originalData: InfiniteData<VoicemailQueryData>;
  }) => void;
}) => {
  if (!voicemailData) {
    return;
  }

  return produce(voicemailData, (draft) => {
    let voicemailIndexInPage = -1;
    let pageIndex = -1;

    for (let i = 0; i < draft.pages.length; i++) {
      const page = draft.pages[i];
      const indexInPage = page.rows.findIndex((row) => row.voicemailId === voicemailId);

      if (indexInPage !== -1) {
        voicemailIndexInPage = indexInPage;
        pageIndex = i;
        break;
      }
    }
    if (voicemailIndexInPage === -1 && bailIfNotFound) {
      // don't do anything if voicemail is not found
      return;
    }

    const voicemail = draft.pages[pageIndex]?.rows[voicemailIndexInPage];

    callbackFn({
      voicemailRow: voicemail,
      voicemailIndexInPage: voicemailIndexInPage,
      voicemailPage: draft.pages[pageIndex],
      originalData: draft,
    });

    // Restructure pages to respect page size after mutation
    const allRows: VoicemailTableDataRow[] = [];

    // Collect all rows from all pages
    for (const page of draft.pages) {
      allRows.push(...page.rows);
    }

    const lastPage = draft.pages[draft.pages.length - 1];

    // Clear existing pages
    draft.pages = [];

    // Redistribute rows into pages of correct size
    for (let i = 0; i < allRows.length; i += pageSize) {
      const pageRows = allRows.slice(i, i + pageSize);
      const lastRow = pageRows[pageRows.length - 1];

      const hasMoreRows = i + pageSize < allRows.length;
      draft.pages.push({
        rows: pageRows,
        meta: {
          lastId: lastRow?.voicemailId,
          createdAt: lastRow?.createdAt,
          hasNext: hasMoreRows || (lastPage?.meta.hasNext ?? false),
        },
      });
    }
  });
};

// This function updates the voicemail data in the react query cache based on the voicemail event
// to be re-rendered in the UI
export const updateVoicemailData = (
  event: VoicemailUpdateEvent,
  voicemailsData?: InfiniteData<VoicemailQueryData>
): InfiniteData<VoicemailQueryData> | undefined => {
  switch (event.type) {
    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_READ:
    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_UNREAD: {
      if (!voicemailsData) {
        return;
      }
      let voicemailFound = false;
      const newVoicemailsData = produce(voicemailsData, (draft) => {
        const voicemailRow = draft.pages
          .map((page) => page.rows)
          .flat()
          .find((row) => row.voicemailId === event.voicemail?.id);
        if (!voicemailRow) {
          // don't do anything if voicemail is not found
          return;
        }
        voicemailFound = true;
        if (event.type === VoicemailEventType.VOICEMAIL_EVENT_TYPE_READ) {
          voicemailRow.readAt = event.voicemail?.readEpoch?.toString() ?? new Date().toISOString();
        } else {
          voicemailRow.readAt = '';
        }
      });

      if (!voicemailFound) {
        return;
      }

      return newVoicemailsData;
    }
    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_DELETE: {
      if (!voicemailsData) {
        return;
      }

      let voicemailFound = false;

      const newVoicemailsData = produce(voicemailsData, (draft) => {
        for (const page of draft.pages) {
          const voicemailIndex = page.rows.findIndex((vm) => vm.voicemailId === event.voicemail?.id);
          if (voicemailIndex !== -1) {
            page.rows.splice(voicemailIndex, 1);
            voicemailFound = true;
            break; // Stop iterating as soon as the voicemail is found and removed
          }
        }
      });
      if (!voicemailFound) {
        // don't do anything if voicemail is not found
        return;
      }
      return newVoicemailsData;
    }
    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_NEW: {
      //onNewVoicemailMessage(event);
      return;
    }
    default:
      return;
  }
};

// when a new voicemail message is received, it calls an endpoint to get the hydrated voicemail message and adds it to the cache
export const useAddVoicemailMessage = (
  getVoicemailsData: () => InfiniteData<VoicemailQueryData> | undefined,
  setVoicemailData: (data: InfiniteData<VoicemailQueryData>) => void,
  selectedLocationIdsExcludingParent: string[],
  mappedTags: Record<string, Tag>,
  mailboxToNameMap: Record<string, string>
) => {
  const [newVoicemailEvent, setNewVoicemailEvent] = useState<VoicemailUpdateEvent | undefined>(undefined);

  const { refetch } = useScopedQuery({
    queryKey: queryKeys.voicemailMessage(newVoicemailEvent?.mailboxId, newVoicemailEvent?.voicemailId),
    queryFn: () => VoicemailApi.getVoicemail({ messageId: newVoicemailEvent?.voicemailId ?? '' }),
    enabled: false, // This is disabled by default, and will be enabled when a new voicemail message is received
    onSuccess: (data) => {
      if (!data || !data.voicemail) {
        return;
      }
      const newVoicemail = createVoicemailObject(
        data.voicemail,
        selectedLocationIdsExcludingParent,
        mappedTags,
        mailboxToNameMap
      );
      const voicemailsData = getVoicemailsData();
      if (!voicemailsData) {
        console.log('voicemail realtime update failed. voicemailsData is not available in query cache');
        return;
      }

      const newVoicemailData = produce(voicemailsData, (draft) => {
        if (newVoicemail.forwardedMessageId) {
          // if it's a forwarded message, add the voicemail right before the original voicemail
          for (const page of draft.pages) {
            // the channelID will be the same for the original voicemail and the forwarded voicemail
            // in case the original voicemail was deleted, then use createdAt to find the correct position
            const voicemailIndex = page.rows.findIndex(
              (vm) => vm.channelId === newVoicemail.channelId || vm.createdAt < newVoicemail.createdAt
            );
            if (voicemailIndex !== -1) {
              page.rows.splice(voicemailIndex, 0, newVoicemail);
              break; // Stop iterating as soon as the voicemail is found
            }
          }
        } else {
          if (draft.pages.length === 0) {
            // if there are no pages in the voicemails, then add a new page
            draft.pages.push({
              rows: [],
              meta: {
                lastId: undefined,
                createdAt: undefined,
                hasNext: false,
              },
            });
          }
          // if it's a new voicemail, add it at the beginning of the list
          draft.pages[0]?.rows.unshift(newVoicemail);
          draft.pages[0].meta = {
            lastId: newVoicemail.voicemailId,
            createdAt: newVoicemail.createdAt,
            hasNext: false,
          };
        }
      });

      setVoicemailData(newVoicemailData);
    },
    retry: 3,
    retryDelay: (attempt) => {
      // In real time update, sometimes the voicemail message is not available immediately after the event is received
      // Exponential backoff with a maximum delay of 2 minutes (120000 ms)
      const delay = Math.min(4 ** attempt * 1000, 120000);
      console.log(`Retrying to fetch voicemail message in ${delay} ms. Attempt ${attempt}`);
      return delay;
    },
  });

  useEffect(() => {
    if (newVoicemailEvent) {
      refetch();
    }
  }, [newVoicemailEvent, refetch]);

  const onNewVoicemailMessage = (event: VoicemailUpdateEvent) => {
    setNewVoicemailEvent(event);
  };

  return { onNewVoicemailMessage };
};

export const updateVoicemailTagsInVoicemails = ({
  voicemailMessageId,
  tagIds,
  mappedTags,
  queryClient,
  voicemailQueryKey,
}: {
  voicemailMessageId: string;
  tagIds: string[];
  mappedTags: Record<string, Tag>;
  queryClient: QueryClient;
  voicemailQueryKey: (string | string[] | undefined)[];
}) => {
  const voicemailsData = queryClient.getQueryData(voicemailQueryKey) as InfiniteData<VoicemailQueryData>;
  if (!voicemailsData) {
    return;
  }

  const updatedPages = voicemailsData.pages.map((page) => {
    const updatedData = page.rows.map((voicemailData: VoicemailTableDataRow) => {
      if (voicemailData.voicemailId === voicemailMessageId) {
        const updatedTagsIds =
          voicemailData.tagIds && voicemailData.tagIds.length > 0
            ? Array.from(new Set([...voicemailData.tagIds, ...tagIds]))
            : tagIds;
        const updatedTags = updatedTagsIds.map((tagId) => mappedTags[tagId]);
        return {
          ...voicemailData,
          tagIds: updatedTagsIds,
          tags: updatedTags,
        };
      }
      return voicemailData;
    });

    return { ...page, data: updatedData };
  });

  const updatedData: InfiniteData<VoicemailQueryData> = {
    ...voicemailsData,
    pages: updatedPages,
  };

  queryClient.setQueryData(voicemailQueryKey, updatedData);
};
